import type {ReactElement} from 'react';
import {useRef} from 'react';
import styles from './ai-chat-window-inspirations.module.scss';
import {Text, TextSize} from '@Components/text/text';
import {Icon} from '@Components/icon-v2';
import {AiChatInspiration} from './components/ai-chat-inspiration';
import type {AiChatWindowInspirationsProps} from './ai-chat-window-inspirations.types';
import {INSPIRATIONS_WIDTH_QUERY, InspirationsWidth} from './ai-chat-window-inspirations.types';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';

export function AiChatWindowInspirations({inspirations, cardsDecoration}: AiChatWindowInspirationsProps): ReactElement {
  const {state} = useAiChat();
  const {isCompact} = useAiChatSize();
  const containerRef = useRef<HTMLDivElement>(null);
  const inspirationsWidthMatches = useContainerQuery(containerRef, INSPIRATIONS_WIDTH_QUERY);

  const getInspirationsWidth = (): InspirationsWidth => {
    if (inspirationsWidthMatches[InspirationsWidth.ExtraNarrow]) {
      return InspirationsWidth.ExtraNarrow;
    }

    if (inspirationsWidthMatches[InspirationsWidth.Narrow]) {
      return InspirationsWidth.Narrow;
    }

    return InspirationsWidth.Regular;
  };

  const inspirationsWidth = getInspirationsWidth();

  return (
    <div ref={containerRef} className={`${styles.container} ${isCompact ? styles.isCompact : ''}`}>
      <div className={styles.backgroundGradientClip}>
        <div className={`${styles.backgroundGradient} ${state.isSidebarExpanded ? styles.mellow : ''}`} />
      </div>
      <div className={`${styles.content} ${isCompact ? styles.isCompact : ''}`}>
        {cardsDecoration}
        <div className={styles.header}>
          <Text className={styles.headerText} size={TextSize.LARGE} bold val={window.i18next.t('pmwjs_ai_chat_see_what_you_can_create')} />
          <Icon icon="icon-sparkle-filled" type={IconType.GHOST_UNCOLORED} className={styles.headerSparkle} size={IconSize.SIZE_ICON_24} />
        </div>
        <div className={`${styles.inspirationsGrid} ${isCompact ? styles.isCompact : ''}`}>
          {inspirations.map((inspiration, index) => (
            <AiChatInspiration key={index} {...inspiration} inspirationsWidth={inspirationsWidth} />
          ))}
        </div>
      </div>
    </div>
  );
}
